import React from 'react';
import {
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  EditButton,
  FormDataConsumer,
  SelectInput,
  TextInput,
  useGetList,
  useRecordContext,
  useResourceContext,
  required,
} from 'react-admin';
import HNForm from '../components/HNForm';
import HNList from '../components/HNList';
import { useNavigate } from 'react-router-dom';
import { ParameterKeyEnum } from '../utils/parameter.types';

interface ParameterKeyFieldRecord {
  key: string;
  description?: string;
  value: string;
  not_used: boolean;
  key_prisma?: ParameterKeyEnum;
}

const KeyInput: React.FC<{ mode: 'create' | 'update' }> = ({ mode }) => {
  const [inputMode, setInputMode] = React.useState<'enum' | 'custom'>('enum');
  const { data: existingParameters } = useGetList('parameter', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'key', order: 'ASC' },
  });

  if (mode === 'update') {
    return <TextInput source="key" label="Key" fullWidth disabled />;
  }

  const allKeys = Object.values(ParameterKeyEnum);
  const availableKeys = allKeys.filter((key) => !existingParameters?.some((param: any) => param.key === key));

  return (
    <div>
      <SelectInput
        source="inputMode"
        label="Parameter Type"
        choices={[
          { id: 'enum', name: 'Standard Parameter (from predefined list)' },
          { id: 'custom', name: 'Custom Parameter (new key)' },
        ]}
        defaultValue="enum"
        onChange={(e) => setInputMode(e.target.value as 'enum' | 'custom')}
        fullWidth
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.inputMode === 'custom' ? (
            <TextInput
              source="key"
              label="Custom Key"
              helperText="Use snake_case, e.g., 'my_new_parameter'. This will create a new parameter not tied to the app enum."
              validate={required()}
              fullWidth
            />
          ) : (
            <SelectInput
              source="key"
              label="Standard Key"
              choices={availableKeys.map((key) => ({
                id: key,
                name: key,
              }))}
              validate={required()}
              fullWidth
            />
          )
        }
      </FormDataConsumer>
    </div>
  );
};

const ParameterForm: React.FC<any> = ({ mode, ...props }) => (
  <HNForm {...props}>
    <KeyInput mode={mode} />
    <TextInput source="description" label="Description" fullWidth />
    <TextInput source="value" label="Value" fullWidth multiline />
    <BooleanInput source="not_used" label="Not Used" />
  </HNForm>
);

const ParameterTitle: React.FC = () => {
  const record = useRecordContext<ParameterKeyFieldRecord>();
  return <span>{record && record.key ? `Parameter "${record.description || record.key}"` : 'Create Parameter'}</span>;
};

const ParameterKeyField: React.FC<any> = () => {
  const record = useRecordContext<ParameterKeyFieldRecord>();
  // const isCustom = !record?.key_prisma;
  return (
    <span>
      {record?.description || record?.key}
      {/* {isCustom && <span style={{ marginLeft: 8, color: '#666', fontSize: '0.85em' }}>(custom)</span>} */}
    </span>
  );
};

const ParameterValueField: React.FC<any> = () => {
  const record = useRecordContext<ParameterKeyFieldRecord>();
  return <span>{record?.not_used ? 'Not set' : record?.value}</span>;
};

export const ParameterList: React.FC<any> = (props) => (
  <HNList {...props} bulkActionButtons={false} sort={{ field: 'key', order: 'ASC' }}>
    <Datagrid>
      <ParameterKeyField source="key" sortable />
      <ParameterValueField source="value" />
      <EditButton />
    </Datagrid>
  </HNList>
);

export const ParameterCreate: React.FC<any> = (props) => {
  const navigate = useNavigate();
  const resource = useResourceContext();

  return (
    <Create
      title="Create Parameter"
      {...props}
      transform={(data: any) => {
        // Remove inputMode helper field
        const { inputMode, ...paramData } = data;
        return paramData;
      }}
      mutationOptions={{
        onSuccess: () => {
          navigate(`/${resource}`);
        },
      }}>
      <ParameterForm mode="create" />
    </Create>
  );
};

export const ParameterEdit: React.FC<any> = (props) => (
  <Edit title={<ParameterTitle />} {...props}>
    <ParameterForm mode="update" />
  </Edit>
);
